import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGrippContext } from '../../grippContext';
import { StorageKeys, useStorageKeys } from '../../storage';
import { useCurrentWorkspace } from '../../auth';

export const useSetFilter = (
  queryKey: string,
  storageKey: string,
  isMultiWorkspace?: boolean,
  workspaceOverride?: string
) => {
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();

  const workspace = workspaceOverride ?? (isMultiWorkspace
    ? currentWorkspace === null
      ? 'ME'
      : currentWorkspace?.id
    : undefined);

  return useMutation({
    mutationKey: [queryKey],
    mutationFn: async (filter: any) => {
      const key = storageKeys[storageKey as keyof StorageKeys];
      
      // Handle ActivityFilterModel instances by calling toJSON() if available
      const filterToSave = filter && typeof filter.toJSON === 'function' ? filter.toJSON() : filter;
      
      if (!isMultiWorkspace) {
        await storageHandler.setItem(key, JSON.stringify(filterToSave));
      } else if (workspace) {
        const raw = await storageHandler.getItem(key);

        let updatedData: { workspace: string; filter: any }[] = [];

        const parsed = JSON.parse(raw as string);

        if (
          Array.isArray(parsed) &&
          parsed.every(
            (item) =>
              item &&
              typeof item === 'object' &&
              'workspace' in item &&
              'filter' in item
          )
        ) {
          // Valid format, update or add
          const existingIndex = parsed.findIndex(
            (item) => item.workspace === workspace
          );

          if (existingIndex >= 0) {
            // Update existing
            parsed[existingIndex].filter = filterToSave;
          } else {
            // Add new
            parsed.push({ workspace, filter: filterToSave });
          }

          updatedData = parsed;
        } else {
          // Old format, overwrite
          updatedData = [{ workspace, filter: filterToSave }];
        }

        await storageHandler.setItem(key, JSON.stringify(updatedData));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKey],
        refetchType: 'all',
      });
    },
  });
};
